import { validateContentBeforeSave } from "@/lib/contentLint";
import { createFileRoute } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { BrandSeal } from "@/components/brand/BrandSeal";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { Pencil, Plus, Upload, Search } from "lucide-react";
import {
  adminListSubscribers,
  type AdminSubscriberRow,
} from "@/lib/leadMagnet.functions";

export const Route = createFileRoute("/admin/training")({
  head: () => ({ meta: [{ title: "Academy CMS — Command Center" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole={"admin"}>
      <PortalShell title="Academy CMS" nav={ADMIN_NAV} tourKey="admin_tour">
        <TrainingCMS />
      </PortalShell>
    </ProtectedRoute>
  ),
});

const AUDIENCES = ["public", "client", "agent"] as const;
type Audience = (typeof AUDIENCES)[number];

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);
}

function TrainingCMS() {
  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-2">
        <div>
          <Eyebrow>Academy CMS</Eyebrow>
          <h2 className="mt-2 font-display font-semibold text-navy-800" style={{ fontSize: "32px", letterSpacing: "-0.01em" }}>
            Author the <em className="text-bronze-600 italic font-normal">Curriculum</em>
          </h2>
          <p className="mt-2 font-sans text-[14px] text-charcoal-500 max-w-2xl">
            Publish tracks, lessons, and glossary terms across public, client, and strategist audiences.
          </p>
        </div>
      </div>

      <Tabs defaultValue="lessons" className="mt-8">
        <TabsList className="bg-cream-50 border border-bronze-600/20">
          <TabsTrigger value="lessons">Lessons</TabsTrigger>
          <TabsTrigger value="tracks">Tracks</TabsTrigger>
          <TabsTrigger value="glossary">Glossary</TabsTrigger>
          <TabsTrigger value="family-library">Family Library</TabsTrigger>
          <TabsTrigger value="subscribers">Subscribers</TabsTrigger>
        </TabsList>

        <TabsContent value="lessons" className="mt-6"><LessonsTab /></TabsContent>
        <TabsContent value="tracks" className="mt-6"><TracksTab /></TabsContent>
        <TabsContent value="glossary" className="mt-6"><GlossaryTab /></TabsContent>
        <TabsContent value="family-library" className="mt-6"><FamilyLibraryTab /></TabsContent>
        <TabsContent value="subscribers" className="mt-6"><SubscribersTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// EDUCATION SUBSCRIBERS (lead magnet email capture)
// ============================================================

function SubscribersTab() {
  const listFn = useServerFn(adminListSubscribers);
  const [rows, setRows] = useState<AdminSubscriberRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setRows(await listFn());
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to load subscribers");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const optedIn = rows.filter((r) => r.consent_marketing && !r.unsubscribed_at).length;
  const downloads = rows.reduce((n, r) => n + r.downloads, 0);

  function exportCsv() {
    const head = ["email", "first_name", "consent_marketing", "unsubscribed_at", "source", "downloads", "created_at"];
    const body = rows.map((r) =>
      [r.email, r.first_name ?? "", r.consent_marketing, r.unsubscribed_at ?? "", r.source_title ?? "", r.downloads, r.created_at]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(","),
    );
    const blob = new Blob([[head.join(","), ...body].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `education-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-display text-[28px] text-navy-800">Education Subscribers</h3>
          <p className="mt-1 font-sans text-[13px] text-charcoal-500">
            Emails captured from the featured Family Library download. Educational consent only — no CRM lead is created.
          </p>
        </div>
        <SecondaryButton onClick={exportCsv}>Export CSV</SecondaryButton>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[
          { label: "Subscribers", value: rows.length },
          { label: "Opted in", value: optedIn },
          { label: "Downloads", value: downloads },
        ].map((k) => (
          <div key={k.label} className="border border-bronze-600/20 bg-white p-4 shadow-soft" style={{ borderRadius: 4 }}>
            <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">{k.label}</p>
            <p className="mt-1 font-display text-[28px] text-navy-800">{k.value.toLocaleString()}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <p className="font-sans text-[13px] text-charcoal-500">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="border border-bronze-600/20 bg-white p-10 text-center shadow-soft" style={{ borderRadius: 4 }}>
          <p className="font-display text-[20px] text-navy-800">No subscribers yet</p>
          <p className="mt-2 font-sans text-[13px] text-charcoal-500">
            Mark a Family Library resource as the featured lead magnet to start capturing emails.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-bronze-600/20 bg-white shadow-soft" style={{ borderRadius: 4 }}>
          <table className="w-full min-w-[640px]">
            <thead className="border-b border-bronze-600/20 bg-cream-50">
              <tr className="text-left font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">
                <th className="px-4 py-3">Email</th>
                <th className="w-32 px-4 py-3">Name</th>
                <th className="w-40 px-4 py-3">Source</th>
                <th className="w-24 px-4 py-3">Consent</th>
                <th className="w-24 px-4 py-3">Downloads</th>
                <th className="w-28 px-4 py-3">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-bronze-600/15">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-cream-50/60">
                  <td className="px-4 py-3 font-sans text-[13px] text-charcoal-900">{r.email}</td>
                  <td className="px-4 py-3 font-sans text-[13px] text-charcoal-500">{r.first_name ?? "—"}</td>
                  <td className="px-4 py-3 font-sans text-[12px] text-charcoal-500">{r.source_title ?? "—"}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-[0.14em] ${
                        r.unsubscribed_at
                          ? "bg-cream-50 text-charcoal-500 border border-bronze-600/30"
                          : r.consent_marketing
                            ? "bg-navy-800 text-cream-50"
                            : "border border-bronze-600/40 text-charcoal-900"
                      }`}
                      style={{ borderRadius: 2 }}
                    >
                      {r.unsubscribed_at ? "Unsub" : r.consent_marketing ? "Opted in" : "File only"}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-sans text-[13px] text-charcoal-900">{r.downloads}</td>
                  <td className="px-4 py-3 font-sans text-[12px] text-charcoal-500">
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// LESSONS
// ============================================================

interface LessonRow {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  body: string | null;
  track_id: string;
  lesson_type: string;
  estimated_minutes: number;
  order_index: number;
  audience: string[];
  published: boolean;
  featured: boolean;
  cover_image_url: string | null;
  video_url: string | null;
}

interface TrackOption { id: string; title: string }

function LessonsTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState<LessonRow[]>([]);
  const [tracks, setTracks] = useState<TrackOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [trackFilter, setTrackFilter] = useState("all");
  const [audienceFilter, setAudienceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<LessonRow | null>(null);

  async function load() {
    setLoading(true);
    const [l, t] = await Promise.all([
      supabase.from("academy_lessons").select("*").order("order_index"),
      supabase.from("academy_tracks").select("id, title").order("title"),
    ]);
    setRows((l.data ?? []) as any);
    setTracks((t.data ?? []) as any);
    setLoading(false);
  }
  useEffect(() => { void load(); }, []);

  const trackMap = useMemo(() => new Map(tracks.map((t) => [t.id, t.title])), [tracks]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (trackFilter !== "all" && r.track_id !== trackFilter) return false;
    if (audienceFilter !== "all" && !(r.audience ?? []).includes(audienceFilter)) return false;
    if (statusFilter === "published" && !r.published) return false;
    if (statusFilter === "draft" && r.published) return false;
    if (search && !r.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [rows, trackFilter, audienceFilter, statusFilter, search]);

  function newLesson(): LessonRow {
    return {
      id: "", title: "", slug: "", excerpt: "", body: "",
      track_id: tracks[0]?.id ?? "", lesson_type: "article",
      estimated_minutes: 5, order_index: rows.length,
      audience: ["public"], published: false, featured: false,
      cover_image_url: null, video_url: null,
    };
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search lessons…"
            className="pl-9 w-64 bg-white border-bronze-600/30"
          />
        </div>
        <Select value={trackFilter} onValueChange={setTrackFilter}>
          <SelectTrigger className="w-48 bg-white border-bronze-600/30"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tracks</SelectItem>
            {tracks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={audienceFilter} onValueChange={setAudienceFilter}>
          <SelectTrigger className="w-40 bg-white border-bronze-600/30"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All audiences</SelectItem>
            {AUDIENCES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 bg-white border-bronze-600/30"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
          </SelectContent>
        </Select>
        <span className="flex-1" />
        <PrimaryButton onClick={() => setEditing(newLesson())}>
          <Plus size={14} className="mr-1 inline" /> New Lesson
        </PrimaryButton>
      </div>

      {loading ? (
        <p className="font-sans text-[13px] text-charcoal-500 p-6">Loading…</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No lessons yet"
          message="Publish your first Academy lesson to start the curriculum."
          actionLabel="New Lesson"
          onAction={() => setEditing(newLesson())}
        />
      ) : (
        <div className="bg-white border border-bronze-600/20 rounded-[4px] shadow-soft overflow-hidden">
          <table className="w-full">
            <thead className="bg-cream-50 border-b border-bronze-600/20">
              <tr className="text-left font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Track</th>
                <th className="px-4 py-3">Audience</th>
                <th className="px-4 py-3 w-16 text-center">Order</th>
                <th className="px-4 py-3 w-24">Status</th>
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-bronze-600/15">
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-cream-50/60 cursor-pointer" onClick={() => setEditing(r)}>
                  <td className="px-4 py-3">
                    <p className="font-sans text-[13px] text-charcoal-900">{r.title}</p>
                    <p className="font-sans text-[11px] text-charcoal-500">{r.slug}</p>
                  </td>
                  <td className="px-4 py-3 font-sans text-[12px] text-charcoal-900">{trackMap.get(r.track_id) || "—"}</td>
                  <td className="px-4 py-3 font-sans text-[11px]">
                    <div className="flex gap-1">
                      {(r.audience ?? []).map((a) => (
                        <span key={a} className="px-1.5 py-0.5 rounded-sm bg-bronze-400/15 text-bronze-600 uppercase tracking-[0.12em] text-[10px]">{a}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center font-display text-[14px] text-navy-800">{r.order_index}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full font-sans text-[10px] uppercase tracking-[0.12em] ${
                      r.published ? "bg-success/15 text-success" : "bg-charcoal-500/15 text-charcoal-500"
                    }`}>
                      {r.published ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="px-4 py-3"><Pencil size={14} className="text-charcoal-500" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <LessonEditor
          lesson={editing}
          tracks={tracks}
          onClose={() => setEditing(null)}
          onSaved={async (msg) => {
            await load();
            setEditing(null);
            if (user?.id) await supabase.from("activity_logs").insert({
              user_id: user.id, action: "admin.lesson_saved", target_type: "academy_lesson",
            });
            toast.success(msg);
          }}
        />
      )}
    </div>
  );
}

function LessonEditor({
  lesson, tracks, onClose, onSaved,
}: {
  lesson: LessonRow; tracks: TrackOption[];
  onClose: () => void; onSaved: (msg: string) => void;
}) {
  const [form, setForm] = useState<LessonRow>(lesson);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isNew = !form.id;

  function update<K extends keyof LessonRow>(k: K, v: LessonRow[K]) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  async function save(publish?: boolean) {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.track_id) return toast.error("Select a track");
    setSaving(true);
    const payload: any = {
      title: form.title.trim(),
      slug: form.slug || slugify(form.title),
      excerpt: form.excerpt || null,
      body: form.body || null,
      track_id: form.track_id,
      lesson_type: form.lesson_type,
      estimated_minutes: form.estimated_minutes,
      order_index: form.order_index,
      audience: form.audience.length ? form.audience : ["public"],
      published: publish ?? form.published,
      cover_image_url: form.cover_image_url,
      video_url: form.video_url,
    };
    const compliance = validateContentBeforeSave({ title: form.title, excerpt: form.excerpt ?? null, body_md: form.body ?? null });
    if (compliance.blocked) {
      compliance.messages.slice(0, 3).forEach((m) => toast.error(m));
      return;
    }
    const { error } = isNew
      ? await supabase.from("academy_lessons").insert(payload)
      : await supabase.from("academy_lessons").update(payload).eq("id", form.id);
    setSaving(false);
    if (error) return toast.error(error.message);
    onSaved(isNew ? "Lesson created" : publish ? "Lesson published" : "Lesson saved");
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const ext = file.name.split(".").pop();
    const path = `lessons/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error } = await supabase.storage.from("academy-images").upload(path, file, { upsert: false });
    if (error) {
      setUploading(false);
      return toast.error(error.message);
    }
    const { data } = supabase.storage.from("academy-images").getPublicUrl(path);
    update("cover_image_url", data.publicUrl);
    setUploading(false);
    toast.success("Image uploaded");
  }

  function toggleAudience(a: Audience) {
    update("audience", form.audience.includes(a) ? form.audience.filter((x) => x !== a) : [...form.audience, a]);
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-navy-800">
            {isNew ? "New Lesson" : "Edit Lesson"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Title</Label>
            <Input
              value={form.title}
              onChange={(e) => {
                update("title", e.target.value);
                if (!form.id && (!form.slug || form.slug === slugify(form.title))) update("slug", slugify(e.target.value));
              }}
              className="border-bronze-600/30"
              placeholder="e.g. The Anatomy of Whole Life"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Slug</Label>
              <Input value={form.slug} onChange={(e) => update("slug", slugify(e.target.value))} className="border-bronze-600/30" />
            </div>
            <div>
              <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Track</Label>
              <Select value={form.track_id} onValueChange={(v) => update("track_id", v)}>
                <SelectTrigger className="border-bronze-600/30 bg-white"><SelectValue placeholder="Select a track" /></SelectTrigger>
                <SelectContent>
                  {tracks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Excerpt</Label>
            <Textarea
              value={form.excerpt ?? ""}
              onChange={(e) => update("excerpt", e.target.value)}
              rows={2}
              className="border-bronze-600/30"
              placeholder="One-line summary shown in cards."
            />
          </div>

          <div>
            <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Body</Label>
            <RichTextEditor value={form.body ?? ""} onChange={(html) => update("body", html)} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Type</Label>
              <Select value={form.lesson_type} onValueChange={(v) => update("lesson_type", v)}>
                <SelectTrigger className="border-bronze-600/30 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="article">Article</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="workbook">Workbook</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Minutes</Label>
              <Input type="number" min={1} value={form.estimated_minutes} onChange={(e) => update("estimated_minutes", Number(e.target.value))} className="border-bronze-600/30" />
            </div>
            <div>
              <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Order</Label>
              <Input type="number" min={0} value={form.order_index} onChange={(e) => update("order_index", Number(e.target.value))} className="border-bronze-600/30" />
            </div>
          </div>

          {form.lesson_type === "video" && (
            <div>
              <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Video URL</Label>
              <Input value={form.video_url ?? ""} onChange={(e) => update("video_url", e.target.value)} placeholder="https://…" className="border-bronze-600/30" />
            </div>
          )}

          <div>
            <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Audience</Label>
            <div className="mt-2 flex gap-2">
              {AUDIENCES.map((a) => {
                const on = form.audience.includes(a);
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => toggleAudience(a)}
                    className={`px-3 py-1 rounded-full font-sans text-[11px] uppercase tracking-[0.14em] border transition-colors ${
                      on ? "bg-navy-800 text-cream-50 border-navy-800" : "bg-white text-charcoal-500 border-bronze-600/30 hover:border-bronze-600"
                    }`}
                  >
                    {a}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Cover Image</Label>
            <div className="mt-2 flex items-center gap-4">
              {form.cover_image_url ? (
                <img src={form.cover_image_url} alt="cover" className="w-32 h-20 object-cover rounded-sm border border-bronze-600/20" />
              ) : (
                <div className="w-32 h-20 bg-cream-50 border border-dashed border-bronze-600/30 rounded-sm flex items-center justify-center font-sans text-[10px] text-charcoal-500 uppercase tracking-[0.14em]">
                  No image
                </div>
              )}
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-bronze-600/30 rounded-sm font-sans text-[12px] text-navy-800 hover:bg-bronze-400/10">
                <Upload size={14} />
                {uploading ? "Uploading…" : "Upload"}
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
              </label>
              {form.cover_image_url && (
                <button type="button" onClick={() => update("cover_image_url", null)} className="font-sans text-[12px] text-danger hover:underline">
                  Remove
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-bronze-600/15">
            <Switch checked={form.published} onCheckedChange={(v) => update("published", v)} />
            <Label className="font-sans text-[12px] text-charcoal-900">Published</Label>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <SecondaryButton onClick={() => void save(false)} disabled={saving}>Save Draft</SecondaryButton>
          <PrimaryButton onClick={() => void save(true)} disabled={saving}>
            {saving ? "Saving…" : "Save & Publish"}
          </PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// TRACKS
// ============================================================

interface TrackRow {
  id: string; slug: string; title: string; subtitle: string | null;
  description: string | null; color: string; icon: string | null;
  order_index: number; published: boolean; audience: string[];
}

function TracksTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState<TrackRow[]>([]);
  const [editing, setEditing] = useState<TrackRow | null>(null);

  async function load() {
    const { data } = await supabase.from("academy_tracks").select("*").order("order_index");
    setRows((data ?? []) as any);
  }
  useEffect(() => { void load(); }, []);

  function blank(): TrackRow {
    return {
      id: "", slug: "", title: "", subtitle: "", description: "",
      color: "protection", icon: null, order_index: rows.length,
      published: false, audience: ["public"],
    };
  }

  async function save() {
    if (!editing) return;
    if (!editing.title.trim()) return toast.error("Title is required");
    const payload: any = {
      slug: editing.slug || slugify(editing.title),
      title: editing.title, subtitle: editing.subtitle, description: editing.description,
      color: editing.color, icon: editing.icon,
      order_index: editing.order_index, published: editing.published,
      audience: editing.audience.length ? editing.audience : ["public"],
    };
    const compliance = validateContentBeforeSave({ title: editing.title, excerpt: editing.subtitle ?? null, body_md: editing.description ?? null });
    if (compliance.blocked) {
      compliance.messages.slice(0, 3).forEach((m) => toast.error(m));
      return;
    }
    const { error } = editing.id
      ? await supabase.from("academy_tracks").update(payload).eq("id", editing.id)
      : await supabase.from("academy_tracks").insert(payload);
    if (error) return toast.error(error.message);
    if (user?.id) await supabase.from("activity_logs").insert({
      user_id: user.id, action: "admin.track_saved", target_type: "academy_track",
    });
    toast.success("Track saved");
    setEditing(null);
    await load();
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <PrimaryButton onClick={() => setEditing(blank())}><Plus size={14} className="mr-1 inline" /> New Track</PrimaryButton>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No tracks yet" message="Tracks group lessons into a learning path." actionLabel="New Track" onAction={() => setEditing(blank())} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rows.map((t) => (
            <button
              key={t.id} onClick={() => setEditing(t)}
              className="text-left bg-white border border-bronze-600/20 rounded-[4px] p-5 shadow-soft hover:border-bronze-600 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display font-semibold text-navy-800 text-[18px]">{t.title}</p>
                  {t.subtitle && <p className="mt-1 font-sans text-[12px] text-charcoal-500">{t.subtitle}</p>}
                </div>
                <span className={`px-2 py-0.5 rounded-full font-sans text-[10px] uppercase tracking-[0.12em] ${
                  t.published ? "bg-success/15 text-success" : "bg-charcoal-500/15 text-charcoal-500"
                }`}>
                  {t.published ? "Published" : "Draft"}
                </span>
              </div>
              <p className="mt-3 font-sans text-[11px] text-charcoal-500">Order #{t.order_index} · {(t.audience ?? []).join(", ")}</p>
            </button>
          ))}
        </div>
      )}

      {editing && (
        <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="font-display text-navy-800">{editing.id ? "Edit Track" : "New Track"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Title</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: !editing.id ? slugify(e.target.value) : editing.slug })} className="border-bronze-600/30" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Slug</Label>
                  <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} className="border-bronze-600/30" />
                </div>
                <div>
                  <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Color theme</Label>
                  <Select value={editing.color} onValueChange={(v) => setEditing({ ...editing, color: v })}>
                    <SelectTrigger className="border-bronze-600/30 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="protection">Protection</SelectItem>
                      <SelectItem value="income">Income</SelectItem>
                      <SelectItem value="growth">Growth</SelectItem>
                      <SelectItem value="legacy">Legacy</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Subtitle</Label>
                <Input value={editing.subtitle ?? ""} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} className="border-bronze-600/30" />
              </div>
              <div>
                <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Description</Label>
                <Textarea value={editing.description ?? ""} rows={3} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="border-bronze-600/30" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Order</Label>
                  <Input type="number" value={editing.order_index} onChange={(e) => setEditing({ ...editing, order_index: Number(e.target.value) })} className="border-bronze-600/30" />
                </div>
                <div className="flex items-end gap-2">
                  <Switch checked={editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} />
                  <Label className="font-sans text-[12px] text-charcoal-900">Published</Label>
                </div>
              </div>
              <div>
                <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Audience</Label>
                <div className="mt-2 flex gap-2">
                  {AUDIENCES.map((a) => {
                    const on = editing.audience.includes(a);
                    return (
                      <button key={a} type="button"
                        onClick={() => setEditing({ ...editing, audience: on ? editing.audience.filter((x) => x !== a) : [...editing.audience, a] })}
                        className={`px-3 py-1 rounded-full font-sans text-[11px] uppercase tracking-[0.14em] border transition-colors ${
                          on ? "bg-navy-800 text-cream-50 border-navy-800" : "bg-white text-charcoal-500 border-bronze-600/30 hover:border-bronze-600"
                        }`}
                      >{a}</button>
                    );
                  })}
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <SecondaryButton onClick={() => setEditing(null)}>Cancel</SecondaryButton>
              <PrimaryButton onClick={() => void save()}>Save Track</PrimaryButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ============================================================
// GLOSSARY
// ============================================================

interface GlossaryRow {
  id: string; term: string; slug: string;
  short_definition: string | null; long_definition: string | null;
  category: string; featured: boolean;
}

function GlossaryTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState<GlossaryRow[]>([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<GlossaryRow | null>(null);

  async function load() {
    const { data } = await supabase.from("academy_glossary").select("*").order("term");
    setRows((data ?? []) as any);
  }
  useEffect(() => { void load(); }, []);

  const filtered = useMemo(() =>
    rows.filter((r) => !search || r.term.toLowerCase().includes(search.toLowerCase())),
    [rows, search]);

  function blank(): GlossaryRow {
    return { id: "", term: "", slug: "", short_definition: "", long_definition: "", category: "general", featured: false };
  }

  async function save() {
    if (!editing) return;
    if (!editing.term.trim()) return toast.error("Term is required");
    const payload: any = {
      term: editing.term, slug: editing.slug || slugify(editing.term),
      short_definition: editing.short_definition, long_definition: editing.long_definition,
      category: editing.category, featured: editing.featured,
    };
    const compliance = validateContentBeforeSave({ title: editing.term, excerpt: editing.short_definition ?? null, body_md: editing.long_definition ?? null });
    if (compliance.blocked) {
      compliance.messages.slice(0, 3).forEach((m) => toast.error(m));
      return;
    }
    const { error } = editing.id
      ? await supabase.from("academy_glossary").update(payload).eq("id", editing.id)
      : await supabase.from("academy_glossary").insert(payload);
    if (error) return toast.error(error.message);
    if (user?.id) await supabase.from("activity_logs").insert({
      user_id: user.id, action: "admin.glossary_saved", target_type: "academy_glossary",
    });
    toast.success("Glossary entry saved");
    setEditing(null);
    await load();
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search terms…" className="pl-9 w-64 bg-white border-bronze-600/30" />
        </div>
        <span className="flex-1" />
        <PrimaryButton onClick={() => setEditing(blank())}><Plus size={14} className="mr-1 inline" /> New Term</PrimaryButton>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="No glossary terms yet" message="Define industry terms your families and strategists should know." actionLabel="New Term" onAction={() => setEditing(blank())} />
      ) : (
        <div className="bg-white border border-bronze-600/20 rounded-[4px] shadow-soft overflow-hidden">
          <table className="w-full">
            <thead className="bg-cream-50 border-b border-bronze-600/20">
              <tr className="text-left font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">
                <th className="px-4 py-3">Term</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Short Definition</th>
                <th className="px-4 py-3 w-20">Featured</th>
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-bronze-600/15">
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-cream-50/60 cursor-pointer" onClick={() => setEditing(r)}>
                  <td className="px-4 py-3 font-display text-[15px] text-navy-800">{r.term}</td>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-sm bg-bronze-400/15 text-bronze-600 font-sans text-[10px] uppercase tracking-[0.14em]">{r.category}</span></td>
                  <td className="px-4 py-3 font-sans text-[12px] text-charcoal-500 line-clamp-1 max-w-md">{r.short_definition || "—"}</td>
                  <td className="px-4 py-3 font-sans text-[12px]">{r.featured ? "★" : ""}</td>
                  <td className="px-4 py-3"><Pencil size={14} className="text-charcoal-500" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="font-display text-navy-800">{editing.id ? "Edit Term" : "New Term"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Term</Label>
                <Input value={editing.term} onChange={(e) => setEditing({ ...editing, term: e.target.value, slug: !editing.id ? slugify(e.target.value) : editing.slug })} className="border-bronze-600/30" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Slug</Label>
                  <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} className="border-bronze-600/30" />
                </div>
                <div>
                  <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Category</Label>
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger className="border-bronze-600/30 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="protection">Protection</SelectItem>
                      <SelectItem value="income">Income</SelectItem>
                      <SelectItem value="growth">Growth</SelectItem>
                      <SelectItem value="legacy">Legacy</SelectItem>
                      <SelectItem value="tax">Tax</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Short Definition</Label>
                <Textarea value={editing.short_definition ?? ""} rows={2} onChange={(e) => setEditing({ ...editing, short_definition: e.target.value })} className="border-bronze-600/30" />
              </div>
              <div>
                <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Long Definition</Label>
                <RichTextEditor value={editing.long_definition ?? ""} onChange={(html) => setEditing({ ...editing, long_definition: html })} />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editing.featured} onCheckedChange={(v) => setEditing({ ...editing, featured: v })} />
                <Label className="font-sans text-[12px] text-charcoal-900">Featured</Label>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <SecondaryButton onClick={() => setEditing(null)}>Cancel</SecondaryButton>
              <PrimaryButton onClick={() => void save()}>Save Term</PrimaryButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ============================================================
// Empty State (brand)
// ============================================================

function EmptyState({ title, message, actionLabel, onAction }: { title: string; message: string; actionLabel: string; onAction: () => void }) {
  return (
    <div className="bg-cream-50 border border-bronze-600/20 rounded-[4px] p-12 flex flex-col items-center text-center">
      <BrandSeal size={80} />
      <h3 className="mt-5 font-display font-semibold text-navy-800 text-[22px]">{title}</h3>
      <p className="mt-2 font-sans text-[13px] text-charcoal-500 max-w-md">{message}</p>
      <div className="mt-5">
        <PrimaryButton onClick={onAction}>{actionLabel}</PrimaryButton>
      </div>
    </div>
  );
}

// ============================================================
// FAMILY LIBRARY
// ============================================================

import { useServerFn } from "@tanstack/react-start";
import {
  adminListResources,
  adminUpsertResource,
  adminTogglePublished,
  adminDeleteResource,
  adminSignedUploadUrl,
} from "@/lib/familyLibrary.functions";
import { Trash2 } from "lucide-react";

type FLRow = {
  id: string;
  title: string;
  description: string | null;
  category: string;
  tier_required: string;
  file_path: string | null;
  cover_image_url: string | null;
  thumbnail_url: string | null;
  published: boolean;
  sort_order: number;
  download_count: number;
  is_lead_magnet?: boolean;
  lead_magnet_headline?: string | null;
  lead_magnet_subhead?: string | null;
};

const CAT_OPTIONS = ["workbook", "checklist", "template", "guide"] as const;
const TIER_OPTIONS = ["library", "family", "legacy"] as const;
const CAT_COLOR: Record<string, string> = {
  workbook: "bg-navy-800 text-cream-50",
  checklist: "bg-bronze-600 text-cream-50",
  template: "bg-bronze-400/30 text-bronze-600",
  guide: "bg-cream-50 border border-bronze-600/40 text-navy-800",
};

function FamilyLibraryTab() {
  const { user } = useAuth();
  const listFn = useServerFn(adminListResources);
  const upsertFn = useServerFn(adminUpsertResource);
  const toggleFn = useServerFn(adminTogglePublished);
  const deleteFn = useServerFn(adminDeleteResource);
  const signFn = useServerFn(adminSignedUploadUrl);

  const [rows, setRows] = useState<FLRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<FLRow | null>(null);
  const [uploading, setUploading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const data = await listFn();
      setRows((data ?? []) as unknown as FLRow[]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load resources");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  function blank(): FLRow {
    return {
      id: "",
      title: "",
      description: "",
      category: "workbook",
      tier_required: "library",
      file_path: null,
      cover_image_url: null,
      thumbnail_url: null,
      published: false,
      sort_order: 0,
      download_count: 0,
      is_lead_magnet: false,
      lead_magnet_headline: "",
      lead_magnet_subhead: "",
    };
  }

  async function togglePublished(row: FLRow, published: boolean) {
    try {
      await toggleFn({ data: { id: row.id, published } });
      setRows((r) => r.map((x) => (x.id === row.id ? { ...x, published } : x)));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Update failed");
    }
  }

  async function removeRow(row: FLRow) {
    if (!window.confirm(`Delete "${row.title}"? This also removes the file.`)) return;
    try {
      await deleteFn({ data: { id: row.id } });
      toast.success("Resource deleted");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Delete failed");
    }
  }

  async function handlePdfUpload(file: File) {
    if (!editing) return;
    if (file.type !== "application/pdf") return toast.error("PDF files only");
    if (file.size > 50 * 1024 * 1024) return toast.error("Max 50MB");
    setUploading(true);
    try {
      const { path, token } = await signFn({ data: { filename: file.name } });
      const { error } = await supabase.storage
        .from("family-library-files")
        .uploadToSignedUrl(path, token, file, { contentType: "application/pdf" });
      if (error) throw new Error(error.message);
      setEditing({ ...editing, file_path: path });
      toast.success("PDF uploaded");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function handleCoverUpload(file: File) {
    if (!editing) return;
    if (!file.type.startsWith("image/")) return toast.error("Image files only");
    if (file.size > 5 * 1024 * 1024) return toast.error("Max 5MB");
    setUploading(true);
    try {
      const path = `family-library/${crypto.randomUUID()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const { error } = await supabase.storage
        .from("academy-images")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (error) throw new Error(error.message);
      const { data: pub } = supabase.storage.from("academy-images").getPublicUrl(path);
      setEditing({ ...editing, cover_image_url: pub.publicUrl });
      toast.success("Cover uploaded");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    if (!editing) return;
    if (!editing.title.trim()) return toast.error("Title is required");
    try {
      await upsertFn({
        data: {
          id: editing.id || undefined,
          title: editing.title.trim(),
          description: editing.description?.trim() || null,
          category: editing.category as (typeof CAT_OPTIONS)[number],
          tier_required: editing.tier_required as (typeof TIER_OPTIONS)[number],
          file_path: editing.file_path,
          cover_image_url: editing.cover_image_url,
          sort_order: editing.sort_order ?? 0,
          published: editing.published,
          is_lead_magnet: Boolean(editing.is_lead_magnet),
          lead_magnet_headline: editing.lead_magnet_headline?.trim() || null,
          lead_magnet_subhead: editing.lead_magnet_subhead?.trim() || null,
        },
      });
      toast.success("Resource saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Save failed");
    }
    void user;
  }

  return (
    <div>
      <div className="flex items-end justify-between gap-3 mb-5">
        <div>
          <h3 className="font-display text-[28px] text-navy-800">Family Library</h3>
          <p className="mt-1 font-sans text-[13px] text-charcoal-500">
            Manage downloadable workbooks and checklists.
          </p>
        </div>
        <PrimaryButton onClick={() => setEditing(blank())}>
          <Plus size={14} className="mr-1 inline" /> New Resource
        </PrimaryButton>
      </div>

      {loading ? (
        <p className="font-sans text-[13px] text-charcoal-500">Loading…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No resources yet"
          message="Upload your first workbook, checklist, or guide for the families we serve."
          actionLabel="New Resource"
          onAction={() => setEditing(blank())}
        />
      ) : (
        <div className="bg-white border border-bronze-600/20 rounded-[4px] shadow-soft overflow-hidden">
          <table className="w-full">
            <thead className="bg-cream-50 border-b border-bronze-600/20">
              <tr className="text-left font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3 w-32">Category</th>
                <th className="px-4 py-3 w-32">Tier</th>
                <th className="px-4 py-3 w-24">Published</th>
                <th className="px-4 py-3 w-20">Downloads</th>
                <th className="px-4 py-3 w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-bronze-600/15">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-cream-50/60">
                  <td className="px-4 py-3">
                    <button onClick={() => setEditing(r)} className="text-left font-display text-[15px] text-navy-800 hover:underline">
                      {r.title}
                    </button>
                    {!r.file_path && (
                      <span className="ml-2 font-sans text-[10px] uppercase tracking-[0.14em] text-warning">No file</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-[0.14em] ${CAT_COLOR[r.category] ?? ""}`} style={{ borderRadius: 2 }}>
                      {r.category}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-[0.14em] border border-bronze-600/40 text-charcoal-900" style={{ borderRadius: 2 }}>
                      {r.tier_required}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Switch checked={r.published} onCheckedChange={(v) => void togglePublished(r, v)} />
                  </td>
                  <td className="px-4 py-3 font-sans text-[13px] text-charcoal-900">{r.download_count}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setEditing(r)} aria-label="Edit"><Pencil size={14} className="text-charcoal-500 hover:text-navy-800" /></button>
                      <button onClick={() => void removeRow(r)} aria-label="Delete"><Trash2 size={14} className="text-charcoal-500 hover:text-danger" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-navy-800">
                {editing.id ? "Edit Resource" : "New Resource"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Title</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="border-bronze-600/30" />
              </div>
              <div>
                <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Description</Label>
                <Textarea rows={3} value={editing.description ?? ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="border-bronze-600/30" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Category</Label>
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger className="border-bronze-600/30 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CAT_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Tier Required</Label>
                  <Select value={editing.tier_required} onValueChange={(v) => setEditing({ ...editing, tier_required: v })}>
                    <SelectTrigger className="border-bronze-600/30 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIER_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">PDF File</Label>
                <div className="mt-1 flex items-center gap-3">
                  <label className="inline-flex cursor-pointer items-center gap-2 border border-bronze-600/40 bg-cream-50 px-3 py-2 font-sans text-[12px] uppercase tracking-[0.14em] text-navy-800 hover:bg-bronze-400/20" style={{ borderRadius: 2 }}>
                    <Upload size={14} /> {editing.file_path ? "Replace PDF" : "Upload PDF"}
                    <input type="file" accept="application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void handlePdfUpload(f); }} />
                  </label>
                  {editing.file_path && (
                    <span className="font-sans text-[11px] text-charcoal-500 truncate">{editing.file_path.split("/").pop()}</span>
                  )}
                </div>
                <p className="mt-1 font-sans text-[11px] text-charcoal-500">PDF only · max 50MB</p>
              </div>
              <div>
                <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Cover Image (optional)</Label>
                <div className="mt-1 flex items-center gap-3">
                  <label className="inline-flex cursor-pointer items-center gap-2 border border-bronze-600/40 bg-cream-50 px-3 py-2 font-sans text-[12px] uppercase tracking-[0.14em] text-navy-800 hover:bg-bronze-400/20" style={{ borderRadius: 2 }}>
                    <Upload size={14} /> {editing.cover_image_url ? "Replace Cover" : "Upload Cover"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleCoverUpload(f); }} />
                  </label>
                  {editing.cover_image_url && (
                    <img src={editing.cover_image_url} alt="" className="h-12 w-20 object-cover border border-bronze-600/20" />
                  )}
                </div>
                <p className="mt-1 font-sans text-[11px] text-charcoal-500">Recommended 400×300</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Sort Order</Label>
                  <Input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })} className="border-bronze-600/30" />
                </div>
                <div className="flex items-end gap-2">
                  <Switch checked={editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} />
                  <Label className="font-sans text-[12px] text-charcoal-900">Published</Label>
                </div>
              </div>
              <div className="border-t border-bronze-600/20 pt-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={Boolean(editing.is_lead_magnet)}
                    onCheckedChange={(v) => setEditing({ ...editing, is_lead_magnet: v })}
                  />
                  <Label className="font-sans text-[12px] text-charcoal-900">
                    Featured lead magnet (email capture)
                  </Label>
                </div>
                <p className="mt-1 font-sans text-[11px] text-charcoal-500">
                  Only one resource can be featured. It appears on the Academy and Family Library with an email form.
                </p>
                {editing.is_lead_magnet && (
                  <div className="mt-3 space-y-3">
                    <div>
                      <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Headline</Label>
                      <Input
                        value={editing.lead_magnet_headline ?? ""}
                        onChange={(e) => setEditing({ ...editing, lead_magnet_headline: e.target.value })}
                        placeholder={editing.title || "The Family Protection Blueprint"}
                        className="border-bronze-600/30"
                      />
                    </div>
                    <div>
                      <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Subhead</Label>
                      <Textarea
                        rows={2}
                        value={editing.lead_magnet_subhead ?? ""}
                        onChange={(e) => setEditing({ ...editing, lead_magnet_subhead: e.target.value })}
                        placeholder="What a family will learn from this guide."
                        className="border-bronze-600/30"
                      />
                    </div>
                  </div>
                )}
              </div>
              {uploading && <p className="font-sans text-[12px] text-bronze-600">Uploading…</p>}
            </div>
            <DialogFooter className="gap-2">
              <SecondaryButton onClick={() => setEditing(null)}>Cancel</SecondaryButton>
              <PrimaryButton onClick={() => void save()}>Save Resource</PrimaryButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
